import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Clock, Warehouse, Package, ArrowRight, Sparkles, Wrench } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Truck: Truck,
  Clock: Clock,
  Warehouse: Warehouse,
  Package: Package,
  Sparkles: Sparkles,
  Wrench: Wrench
};

const Services = () => {
  return (
    <section id="services" className="py-24 bg-[#0f1729] relative overflow-hidden">
      {/* Decorative gradient blurs (mirrors WhyChooseUs) */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#3B43AF]/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-[#3B43AF]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#3B43AF]/5 to-transparent rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#3B43AF]/20 text-[#7C83E0] font-semibold text-sm rounded-full mb-6 tracking-wide">Our Services</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">What We Do</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            We specialise in commercial and domestic furniture removals, collections and deliveries.
            Our team is dedicated to providing you with the best possible service.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            const isInternal = service.link && service.link.startsWith('/');
            const LinkWrapper = isInternal ? Link : 'a';
            const linkProps = isInternal 
              ? { to: service.link } 
              : { href: service.link };

            return (
              <LinkWrapper 
                key={service.id}
                {...linkProps}
                className="group relative bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm rounded-2xl p-8 transition-all duration-500 border border-white/10 hover:border-[#3B43AF]/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#3B43AF]/10 block no-underline"
                data-testid={`service-card-${service.id}`}
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-[#3B43AF]/20 group-hover:bg-[#3B43AF] rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 backdrop-blur-sm border border-[#3B43AF]/30 group-hover:shadow-lg group-hover:shadow-[#3B43AF]/30">
                  <IconComponent className="w-8 h-8 text-[#7C83E0] group-hover:text-white transition-colors duration-500" />
                </div>

                {/* Content */}
                <h3 className="font-heading text-2xl font-bold text-white mb-4 group-hover:text-[#7C83E0] transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Link */}
                <span className="inline-flex items-center gap-2 text-[#7C83E0] font-semibold group-hover:gap-3 group-hover:text-white transition-all">
                  Learn More
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </LinkWrapper>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">Need something specific? We're happy to help!</p>
          <a 
            href="tel:01273044565" 
            className="inline-flex items-center gap-2 text-[#7C83E0] hover:text-white font-semibold text-lg hover:gap-3 transition-all"
          >
            Call us to discuss your needs
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
